import type { CompanyDossier as Dossier } from '@fruuit/shared'
import { CLIENT_ACTIONS, DIALOGUE_EVENT_TYPES } from '@fruuit/shared/vocab'
import {
  Badge,
  Button,
  Callout,
  Card,
  Dialog,
  DialogCancel,
  EmptyState,
  LoadingRegion,
  PageHeader,
  Select,
  SkeletonText,
  StatusBadge,
  statusLabel,
  Tabs,
  Textarea,
  useDialog,
  useToast,
} from '@fruuit/ui'
import { Building2, ExternalLink } from 'lucide-react'
import { type FormEvent, type ReactNode, useState } from 'react'
import { Link } from 'react-router'
import { ApiError, api, errorMessage, isApiError } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'
import { useTitle } from '../../lib/useTitle'
import {
  confidenceLabel,
  countryLabel,
  coverageLabelTone,
  formatDate,
  hostOf,
  involvementLabel,
  isLinkable,
  plural,
  rightsLabel,
  sectorLabel,
  severityLabel,
  themeLabel,
  truncate,
} from '../coverage/format'
import { useDossier } from '../dashboard/api'
import { StageTrack } from '../dashboard/StageTrack'

export interface CompanyDossierPageProps {
  issuerId: string
  /** `/app` for investors, `/workspace` for staff. */
  base: '/app' | '/workspace'
  /** Staff client context, if any. */
  org: string | null
  /** Investors may record client actions; staff read them only (R-12). */
  canRecordActions: boolean
}

/** S4 Company dossier (PORT-023, INV-008/009, D-05). */
export function CompanyDossierPage({
  issuerId,
  base,
  org,
  canRecordActions,
}: CompanyDossierPageProps) {
  const dossier = useDossier(issuerId, org)
  useTitle(dossier.data?.issuer.legal_name, 'Company')
  const back =
    base === '/app'
      ? { label: 'Portfolio coverage', href: '/app/portfolio' }
      : { label: 'Coverage', href: `/workspace/coverage${org ? `?org=${org}` : ''}` }

  if (dossier.error) {
    const missing = isApiError(dossier.error, 'not_found')
    return (
      <>
        <PageHeader breadcrumbs={[back]} eyebrow="Company" title="Company not available" />
        <EmptyState
          icon={<Building2 />}
          title={
            missing
              ? 'This company is not available in the current workspace.'
              : 'The company could not be loaded'
          }
          description={
            missing
              ? 'It may not be in your coverage or engagement programme.'
              : errorMessage(dossier.error)
          }
        />
      </>
    )
  }
  if (!dossier.data) {
    return (
      <LoadingRegion label="Loading company">
        <SkeletonText lines={10} />
      </LoadingRegion>
    )
  }
  return (
    <DossierView
      d={dossier.data}
      base={base}
      org={org}
      back={back}
      canRecordActions={canRecordActions}
    />
  )
}

function DossierView({
  d,
  base,
  org,
  back,
  canRecordActions,
}: {
  d: Dossier
  base: '/app' | '/workspace'
  org: string | null
  back: { label: string; href: string }
  canRecordActions: boolean
}) {
  const actionDialog = useDialog()
  const q = org ? `?org=${encodeURIComponent(org)}` : ''
  const caseLink = (id: string) => `${base}/cases/${id}${q}`
  return (
    <>
      <PageHeader
        breadcrumbs={[back, { label: d.issuer.legal_name }]}
        eyebrow={`${sectorLabel(d.issuer.sector)} · ${countryLabel(d.issuer.country)}`}
        title={d.issuer.legal_name}
        description="Research, portfolio exposure and engagement history in one place."
        actions={
          canRecordActions ? (
            <Button onClick={() => actionDialog.open()}>Record client action</Button>
          ) : undefined
        }
      />
      {d.staff ? (
        <Callout
          tone="info"
          className="mb-6"
          title={d.org_id ? 'Viewing as the client sees it' : 'Fruuit-wide view'}
        >
          Last internal search:{' '}
          {d.staff.last_search
            ? `${statusLabel(d.staff.last_search.state)} · ${formatDate(d.staff.last_search.completed_at)}`
            : 'none recorded'}
          {d.staff.pending_assessment
            ? ` · Assessment v${d.staff.pending_assessment.version} is ${statusLabel(d.staff.pending_assessment.state).toLowerCase()}`
            : ''}
          {d.staff.scope ? ` · Scope: ${d.staff.scope}` : ''}
        </Callout>
      ) : null}
      <dl className="mb-8 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCell label="Finding">
          <StatusBadge status={d.summary.finding} label={d.summary.finding} />
          <span className="mt-1 block text-xs text-sage">
            {d.summary.priority ?? 'No priority assigned'}
          </span>
        </SummaryCell>
        <SummaryCell label="Evidence reviewed">
          {formatDate(d.summary.evidence_reviewed)}
        </SummaryCell>
        <SummaryCell label="Next scheduled review">
          {formatDate(d.summary.next_review, 'Not scheduled')}
          {d.summary.coverage_label ? (
            <span className="mt-1 block">
              <Badge tone={coverageLabelTone(d.summary.coverage_label)} dot>
                {d.summary.coverage_label}
              </Badge>
            </span>
          ) : null}
        </SummaryCell>
        <SummaryCell label={d.org_id ? 'Portfolio exposure' : 'Engagements'}>
          {d.org_id
            ? `${plural(d.summary.holdings, 'holding')} · ${plural(d.summary.portfolios, 'portfolio')}`
            : plural(d.engagements.length, 'case')}
        </SummaryCell>
      </dl>

      <Tabs
        key={d.issuer.id}
        label="Company sections"
        items={[
          {
            id: 'overview',
            label: 'Overview',
            content: <Overview d={d} base={base} q={q} caseLink={caseLink} />,
          },
          {
            id: 'engagement',
            label: 'Engagement',
            count: d.objectives.length,
            content: <Engagement d={d} caseLink={caseLink} />,
          },
          {
            id: 'evidence',
            label: 'Evidence',
            count: d.evidence.length,
            content: <Evidence d={d} />,
          },
          { id: 'history', label: 'History', count: d.history.length, content: <History d={d} /> },
          {
            id: 'actions',
            label: 'Client actions',
            content: (
              <ClientActions
                issuerId={d.issuer.id}
                org={org}
                canRecord={canRecordActions}
                onRecord={() => actionDialog.open()}
              />
            ),
          },
        ]}
      />
      {canRecordActions ? (
        <RecordActionDialog
          open={actionDialog.isOpen}
          onClose={actionDialog.close}
          issuerId={d.issuer.id}
          company={d.issuer.legal_name}
        />
      ) : null}
    </>
  )
}

function SummaryCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-surface px-5 py-4">
      <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">{label}</dt>
      <dd className="mt-1.5 text-sm font-semibold text-ink">{children}</dd>
    </div>
  )
}

function Definition({ term, value }: { term: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-[0.06em] text-sage uppercase">{term}</dt>
      <dd className="mt-1 text-sm leading-relaxed text-ink">{value || 'Not recorded'}</dd>
    </div>
  )
}

function Overview({
  d,
  base,
  q,
  caseLink,
}: {
  d: Dossier
  base: string
  q: string
  caseLink: (id: string) => string
}) {
  const a = d.current_assessment
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <div className="space-y-6">
        <Card eyebrow="Fruuit assessment" title={a ? a.finding : 'Research not yet released'}>
          <p className="text-sm leading-relaxed text-ink-soft">
            {a ? a.rationale : 'This company remains in coverage while its evidence is reviewed.'}
          </p>
          {a ? (
            <>
              <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <Definition term="International expectation" value={a.norm} />
                <Definition term="Company involvement" value={involvementLabel(a.involvement)} />
                <Definition term="Severity & significance" value={severityLabel(a.severity)} />
                <Definition term="Confidence & uncertainty" value={confidenceLabel(a.confidence)} />
                <Definition term="Company response & remedy" value={a.remediation} />
                <Definition
                  term="Published"
                  value={`Version ${a.version} · ${formatDate(a.published_at)}`}
                />
              </dl>
              <p className="mt-4 text-xs text-sage">
                Independently reviewed{a.reviewed_by ? ` by ${a.reviewed_by}` : ''}.
              </p>
            </>
          ) : null}
          {a?.finding === 'No material concern identified' ? (
            <Callout className="mt-4">
              No material concern does not mean that every possible concern has been excluded. The
              company stays in the review schedule.
            </Callout>
          ) : null}
        </Card>
        <Card eyebrow="What happens next" title="Next steps">
          {d.next_steps.length === 0 ? (
            <p className="text-sm text-sage">No open next actions on this company’s engagements.</p>
          ) : (
            <ul className="divide-y divide-line">
              {d.next_steps.map((s) => (
                <li
                  key={`${s.kind}-${s.engagement_id}-${s.label}-${s.next_action}`}
                  className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <span className="min-w-0 text-sm">
                    <span className="block font-semibold text-ink">{s.next_action}</span>
                    <Link to={caseLink(s.engagement_id)} className="text-teal hover:underline">
                      {s.kind === 'case' ? 'Case' : 'Objective'}: {s.label}
                    </Link>
                  </span>
                  <span className="shrink-0 text-xs text-sage">Due {formatDate(s.due_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        {d.incidents.length > 0 ? (
          <Card eyebrow="Reviewed evidence" title="Incidents">
            <ul className="space-y-4">
              {d.incidents.map((i) => (
                <li key={i.id} className="text-sm">
                  <p className="font-semibold text-ink">{i.title}</p>
                  <p className="text-xs text-sage">
                    {formatDate(i.event_date)} · {i.location} · {i.legal_status}
                    {i.authority ? ` · ${i.authority}` : ''}
                  </p>
                  <p className="mt-1 leading-relaxed text-ink-soft">{i.description}</p>
                  <p className="mt-1 text-xs text-sage">Company response: {i.company_response}</p>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
      <div className="space-y-6">
        <Card title="Engagement plan">
          {d.engagements.length === 0 ? (
            <p className="text-sm text-sage">
              No active engagement. {a ? `${a.finding}.` : 'Evidence review is ongoing.'}
            </p>
          ) : (
            <ul className="space-y-4">
              {d.engagements.map((e) => (
                <li key={e.id}>
                  <Link
                    to={caseLink(e.id)}
                    className="block rounded-md border border-line p-4 hover:bg-wash"
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold tracking-[0.06em] text-teal-500 uppercase">
                        {themeLabel(e.theme)}
                      </span>
                      <StatusBadge status={e.status} />
                    </span>
                    <span className="mt-1 block font-semibold text-ink">{e.title}</span>
                    <span className="mt-1 block text-xs text-sage">
                      {e.objectives_with_implementation}/{e.objectives} objectives with
                      implementation evidence
                    </span>
                    <span className="mt-1 block text-xs text-sage">
                      Engagement started {formatDate(e.original_start)} · Fruuit coverage since{' '}
                      {formatDate(e.fruuit_start)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Holdings & identifiers">
          {d.holdings.length === 0 ? (
            <p className="text-sm text-sage">
              {d.org_id
                ? 'Covered company without a recorded holding.'
                : 'Open with a client context to see holdings.'}
            </p>
          ) : (
            <ul className="divide-y divide-line text-sm">
              {d.holdings.map((h) => (
                <li key={h.holding_id} className="py-2 first:pt-0 last:pb-0">
                  <span className="block font-medium text-ink">
                    {h.identifier_value}{' '}
                    <span className="text-xs text-sage">{h.identifier_type}</span>
                  </span>
                  <span className="text-xs text-sage">
                    {h.portfolio} · Position {formatDate(h.as_of)} · {statusLabel(h.mapping_state)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {d.issuer.identifiers.length > 0 ? (
            <p className="mt-4 border-t border-line pt-3 text-xs text-sage">
              {d.issuer.identifiers.map((i) => `${i.type} ${i.value}`).join(' · ')}
            </p>
          ) : null}
        </Card>
        {d.related.length > 0 ? (
          <Card title="Related entities">
            <ul className="space-y-1 text-sm">
              {d.related.map((r) => (
                <li key={r.id}>
                  <Link to={`${base}/companies/${r.id}${q}`} className="text-teal hover:underline">
                    {r.legal_name}
                  </Link>{' '}
                  <span className="text-xs text-sage">
                    {r.relation === 'parent' ? 'Parent company' : 'Subsidiary'}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </div>
  )
}

function Engagement({ d, caseLink }: { d: Dossier; caseLink: (id: string) => string }) {
  if (d.engagements.length === 0) {
    return <EmptyState title="No engagement on this company in your programme" />
  }
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card eyebrow="The change we seek" title="Objectives & reviewed progress">
        {d.objectives.length === 0 ? (
          <p className="text-sm text-sage">No objectives have been released yet.</p>
        ) : (
          <ul className="space-y-5">
            {d.objectives.map((o) => (
              <li key={o.id}>
                <p className="font-semibold text-ink">{o.title}</p>
                <p className="mt-0.5 text-sm text-ink-soft">{o.change_request}</p>
                <div className="mt-2">
                  <StageTrack stage={o.stage} />
                </div>
                {o.next_action ? (
                  <p className="mt-1 text-xs text-sage">
                    Next test: {o.next_action} · Due {formatDate(o.due_date)}
                  </p>
                ) : null}
                <Link to={caseLink(o.engagement_id)} className="text-xs text-teal hover:underline">
                  {o.case_title} →
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <div className="space-y-6">
        <Card eyebrow="Independently reviewed" title="Evidence reviews">
          {d.milestones.length === 0 ? (
            <p className="text-sm text-sage">
              No independently reviewed milestone evidence has been released.
            </p>
          ) : (
            <ul className="space-y-4">
              {d.milestones.map((m) => (
                <li key={m.id} className="text-sm">
                  <p className="text-xs text-sage">
                    {formatDate(m.assessment_date)} · {m.objective_title}
                  </p>
                  <StageTrack stage={m.stage} compact />
                  <p className="mt-1 leading-relaxed text-ink-soft">
                    <span className="font-semibold text-ink">What the evidence shows: </span>
                    {m.rationale}
                  </p>
                  <p className="mt-0.5 leading-relaxed text-ink-soft">
                    <span className="font-semibold text-ink">What remains unproven: </span>
                    {m.limitations}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Correspondence">
          {d.timeline.length === 0 ? (
            <p className="text-sm text-sage">No correspondence has been published.</p>
          ) : (
            <ul className="space-y-4">
              {d.timeline.map((t) => (
                <li key={t.id} className="text-sm">
                  <p className="text-xs text-sage">
                    {formatDate(t.event_date)} ·{' '}
                    {(DIALOGUE_EVENT_TYPES.labels as Record<string, string>)[t.event_type] ??
                      t.event_type}
                    {t.substantive ? ' · Substantive response' : ''}
                  </p>
                  <Link
                    to={caseLink(t.engagement_id)}
                    className="font-semibold text-teal hover:underline"
                  >
                    {t.title}
                  </Link>
                  <p className="mt-0.5 leading-relaxed text-ink-soft">{t.fruuit_judgment}</p>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-sage">
            A company response does not automatically advance a milestone.
          </p>
        </Card>
      </div>
    </div>
  )
}

function Evidence({ d }: { d: Dossier }) {
  return (
    <Card
      title="Evidence library"
      actions={<span className="text-sage">{plural(d.evidence.length, 'permitted source')}</span>}
    >
      {d.withheld_reference_count > 0 ? (
        <Callout className="mb-4">
          {plural(d.withheld_reference_count, 'evidence reference')} cited by this research{' '}
          {d.withheld_reference_count === 1 ? 'is' : 'are'} not available to this account.
        </Callout>
      ) : null}
      {d.evidence.length === 0 ? (
        <p className="text-sm text-sage">No released sources are available to this account.</p>
      ) : (
        <ul className="divide-y divide-line">
          {d.evidence.map((s) => (
            <li key={s.id} className="flex gap-4 py-4 first:pt-0 last:pb-0">
              <span className="w-24 shrink-0 text-xs font-semibold tracking-[0.06em] text-teal-500 uppercase">
                {s.source_class}
              </span>
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-ink">
                  {isLinkable(s.url) ? (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal hover:underline"
                    >
                      {s.title} <ExternalLink aria-hidden="true" className="inline size-3.5" />
                    </a>
                  ) : (
                    s.title
                  )}
                </p>
                <p className="mt-1 leading-relaxed text-ink-soft">{truncate(s.summary, 250)}</p>
                <p className="mt-1 text-xs text-sage">
                  Published {formatDate(s.publication_date)} · Reviewed {formatDate(s.retrieved_at)}{' '}
                  · {rightsLabel(s.rights_basis)}
                  {isLinkable(s.url) ? '' : ` · ${hostOf(s.url)}`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function History({ d }: { d: Dossier }) {
  return (
    <Card title="Published assessment history">
      {d.history.length === 0 ? (
        <p className="text-sm text-sage">No published assessment versions yet.</p>
      ) : (
        <ol className="divide-y divide-line">
          {d.history.map((h) => (
            <li key={h.id} className="flex gap-5 py-4 first:pt-0 last:pb-0">
              <span className="w-28 shrink-0 text-sm text-ink">
                {formatDate(h.published_at)}
                <small className="block text-xs text-sage">Version {h.version}</small>
              </span>
              <div className="min-w-0 text-sm">
                <StatusBadge status={h.finding} label={h.finding} />
                <p className="mt-1 font-semibold text-ink">
                  {h.change_reason ?? 'Published assessment'}
                </p>
                <p className="mt-1 leading-relaxed text-ink-soft">{h.rationale}</p>
                {h.reviewed_by ? (
                  <p className="mt-1 text-xs text-sage">Reviewed by {h.reviewed_by}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
      <p className="mt-4 text-xs text-sage">
        Only published versions are listed, so version numbers can skip.
      </p>
    </Card>
  )
}

interface ClientAction {
  id: string
  action: string
  note: string
  status: string
  created_at: string
  owner_name?: string | null
}

/** Client actions belong to the engagement lane's `/client-actions`; absent → a calm note. */
function ClientActions({
  issuerId,
  org,
  canRecord,
  onRecord,
}: {
  issuerId: string
  org: string | null
  canRecord: boolean
  onRecord: () => void
}) {
  const actions = useApiQuery<{ items: ClientAction[] }>(
    ['client-actions', issuerId, org],
    '/client-actions',
    {
      query: { issuer_id: issuerId, org },
    },
  )
  return (
    <Card
      title="Your actions & decisions"
      actions={
        canRecord ? (
          <Button size="sm" onClick={onRecord}>
            Record client action
          </Button>
        ) : undefined
      }
    >
      <p className="mb-4 text-xs leading-relaxed text-sage">
        Private to this client workspace. These records do not change Fruuit’s research finding or
        execute a portfolio instruction.
      </p>
      {actions.isPending ? (
        <SkeletonText lines={3} />
      ) : actions.error || !actions.data || actions.data.items.length === 0 ? (
        <p className="text-sm text-sage">No client actions have been recorded.</p>
      ) : (
        <ul className="divide-y divide-line text-sm">
          {actions.data.items.map((x) => (
            <li key={x.id} className="py-3 first:pt-0">
              <p className="text-xs text-sage">{formatDate(x.created_at)}</p>
              <p className="font-semibold text-ink">{x.action}</p>
              <p className="text-ink-soft">{x.note}</p>
              <p className="text-xs text-sage">
                {x.status}
                {x.owner_name ? ` · ${x.owner_name}` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function RecordActionDialog({
  open,
  onClose,
  issuerId,
  company,
}: {
  open: boolean
  onClose: () => void
  issuerId: string
  company: string
}) {
  const [action, setAction] = useState('')
  const [note, setNote] = useState('')
  const { toast } = useToast()
  const record = useApiMutation(
    (vars: { action: string; note: string }, { requestId }) =>
      api.post('/client-actions', { issuer_id: issuerId, ...vars }, { requestId }),
    { invalidate: [['client-actions', issuerId]] },
  )
  async function submit(event: FormEvent) {
    event.preventDefault()
    try {
      await record.mutateAsync({ action, note })
      setAction('')
      setNote('')
      onClose()
      toast({ tone: 'success', message: 'Client action recorded for discussion.' })
    } catch {
      // Shown in the dialog.
    }
  }
  const unavailable = record.error instanceof ApiError && record.error.status === 404
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Record client action"
      description={`${company}. Recorded for discussion with Fruuit; nothing is sent or executed.`}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="client-action-form" loading={record.isPending}>
            Record action
          </Button>
        </>
      }
    >
      <form id="client-action-form" onSubmit={submit} className="space-y-4">
        <Select
          label="Action"
          required
          value={action}
          onChange={(event) => setAction(event.target.value)}
          placeholder="Choose an action"
          options={CLIENT_ACTIONS.values.map((v) => ({ value: v, label: v }))}
        />
        <Textarea
          label="Note"
          required
          minLength={8}
          maxLength={2000}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          error={
            isApiError(record.error, 'validation_failed')
              ? record.error.fieldErrors.note
              : undefined
          }
        />
        {record.error ? (
          <Callout tone="danger" role="alert">
            {unavailable ? 'Client actions are not available yet.' : errorMessage(record.error)}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
